import type { SkillTreeEmblemLayer, SkillTreeEmblemStroke } from './skill-tree-emblems';
import { WHITE } from './skill-tree-palette';

/**
 * The soft blur under every emblem's glow pass, defined once in each SVG
 * that draws emblems (the tree scene, a skill icon) under an id unique to it.
 */
export function SkillTreeEmblemGlowFilter({ id }: { readonly id: string }): JSX.Element {
    return (
        <filter id={id} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.6" />
        </filter>
    );
}

/** The three passes each emblem stroke is drawn in: a soft glow, the line itself, and a faint white core. */
const EMBLEM_PASSES = [
    { width: 3.2, opacity: 0.55, glow: true },
    { width: 1.3, opacity: 1, glow: false },
    { width: 0.5, opacity: 0.7, glow: false },
] as const;

/** `solidFill` is what a `solid` stroke is filled with in this pass: none in the white core pass, so the core stays a line. */
function EmblemStroke({ stroke, width, solidFill }: { readonly stroke: SkillTreeEmblemStroke; readonly width: number; readonly solidFill?: string }): JSX.Element {
    if (typeof stroke === 'string') return <path d={stroke} strokeWidth={width} />;
    const weight = width * (stroke.width ?? 1);
    const fill = stroke.solid ? solidFill : undefined;
    if ('circle' in stroke) {
        const [cx, cy, r] = stroke.circle;
        return <circle cx={cx} cy={cy} r={r} strokeWidth={weight} strokeDasharray={stroke.dash} fill={fill} />;
    }
    return <path d={stroke.d} strokeWidth={weight} fill={fill} />;
}

interface SkillTreeLineEmblemProps {
    readonly layers: readonly SkillTreeEmblemLayer[];
    readonly color: string;
    /** The dark behind the emblem, which its knockouts are filled with. */
    readonly fill: string;
    readonly scale?: number;
    /** The id of the `SkillTreeEmblemGlowFilter` its glow pass is blurred with, defined in the same SVG. */
    readonly glowFilterId: string;
}

/**
 * A line-art emblem (`skill-tree-emblems.ts`) centered on the origin: a
 * grouping root's, or a skill's at the center of its node.
 */
export function SkillTreeLineEmblem({
    layers,
    color,
    fill,
    scale = 0.95,
    glowFilterId,
}: SkillTreeLineEmblemProps): JSX.Element {
    return (
        <g fill="none" strokeLinecap="round" strokeLinejoin="round" transform={`scale(${scale})`}>
            {layers.map((layer, layerIndex) => (
                <g key={layerIndex} transform={layer.rotate ? `rotate(${layer.rotate})` : undefined}>
                    {layer.knockout && <path d={layer.knockout} fill={fill} stroke={fill} strokeWidth={2.5} />}
                    {EMBLEM_PASSES.map((pass, passIndex) => (
                        <g
                            key={passIndex}
                            stroke={passIndex === 2 ? WHITE : color}
                            strokeOpacity={pass.opacity}
                            filter={pass.glow ? `url(#${glowFilterId})` : undefined}
                        >
                            {layer.lines.map((stroke, strokeIndex) => (
                                <EmblemStroke key={strokeIndex} stroke={stroke} width={pass.width} solidFill={passIndex === 2 ? undefined : color} />
                            ))}
                        </g>
                    ))}
                </g>
            ))}
        </g>
    );
}
